// Saturn's rings round the Earth (?whatif=rings): episode 5 of the Voidmode
// Studios channel and the scene its /v/5 link opens.
//
// The rings are Saturn's own strip (public/textures/saturn_ring.png, see
// "Saturn's rings" in docs/textures/giants.md) at Saturn's proportions: the
// strip runs from 1.15 to 2.2857 planet radii on both planets (8.05 and 16
// against Saturn's radius 7 in celestialData.js), so the A ring's outer edge,
// 136,780 km from Saturn's centre or 2.27 of its 60,268 km equatorial radius,
// falls 14,460 km from the Earth's (on the viewer's 6,371 km mean radius;
// 14,478 on the equatorial). They lie in the Earth's equatorial plane
// (the tilt group's XZ plane, as Saturn's lie in its) and use Saturn's ring
// shader, so the Earth's shadow crosses them on the night side, with the
// slant path through the layer (RING_SLANT) and the strip's own opacity,
// since from the ground they are seen a few degrees off edge on. In the
// Earth's shadow they keep 3 per cent of their light where Saturn's keep 22:
// refraction in the air bends light into the shadow only within about 4 per
// cent of a radius of its edge at their distance. The A and C rings stay
// translucent, as Saturn's do in Cassini's pictures: from space the Earth
// shows through them, only the B ring hides it.
//
// Their shadow on the Earth is analytic. EARTH_RINGS_GLSL follows a point's
// ray to the Sun to the ring plane and takes the strip's normal optical
// depth there along the slant path. The depth is read from the strip's alpha
// into 64 samples (ringTauTable), a uniform table because the globe shader is
// at its 16 texture units. It is compiled into the Earth's surface and air
// shell only under the EARTH_RINGS define (main.js sets it with the rings),
// so every other visit runs the shaders unchanged.

import { DoubleSide, Mesh, Quaternion, RingGeometry, ShaderMaterial, Vector2, Vector3, Vector4 } from 'three';
import { saturnRingFragmentShader, saturnRingVertexShader } from './shaders/rings.js';

export function earthRingsRequested(search) {
  return new URLSearchParams(search || '').get('whatif') === 'rings';
}

// Inner and outer edge of the strip in planet radii (celestialData.saturn).
export const EARTH_RING_SPAN = [8.05 / 7, 16 / 7];
const TAU_SAMPLES = 64;
const TAU_MAX = 6;

// Shared by the Earth's surface and air shell; updateEarthRingUniforms sets
// them every frame.
export const earthRingUniforms = {
  uRingCenter: { value: new Vector3() },
  uRingNormal: { value: new Vector3(0, 1, 0) },
  uRingSunDir: { value: new Vector3(1, 0, 0) },
  uRingRadius: { value: 1 },
  uRingSpan: { value: new Vector2(EARTH_RING_SPAN[0], EARTH_RING_SPAN[1]) },
  uRingTau: { value: Array.from({ length: TAU_SAMPLES / 4 }, () => new Vector4()) },
};

export const EARTH_RINGS_GLSL = /* glsl */ `
#ifdef EARTH_RINGS
uniform vec3 uRingCenter;
uniform vec3 uRingNormal;
uniform vec3 uRingSunDir;
uniform float uRingRadius;
uniform vec2 uRingSpan;
uniform vec4 uRingTau[${TAU_SAMPLES / 4}];
float earthRingTau(int i) {
  return dot(uRingTau[i / 4], vec4(equal(ivec4(i - (i / 4) * 4), ivec4(0, 1, 2, 3))));
}
// The share of the Sun's light the rings let through to p (world space).
float earthRingLight(vec3 p) {
  float mu = dot(uRingSunDir, uRingNormal);
  if (abs(mu) < 1e-4) return 1.0;
  float s = -dot(p - uRingCenter, uRingNormal) / mu;
  if (s <= 0.0) return 1.0;
  float r = length(p + uRingSunDir * s - uRingCenter) / uRingRadius;
  float x = (r - uRingSpan.x) / (uRingSpan.y - uRingSpan.x);
  if (x <= 0.0 || x >= 1.0) return 1.0;
  float f = x * ${(TAU_SAMPLES - 1).toFixed(1)};
  int i = int(f);
  float tau = mix(earthRingTau(i), earthRingTau(min(i + 1, ${TAU_SAMPLES - 1})), f - float(i));
  return exp(-tau / abs(mu));
}
#else
float earthRingLight(vec3 p) { return 1.0; }
#endif
`;

// Normal optical depth at TAU_SAMPLES radii evenly across the strip, from its
// alpha (1 - exp(-tau) face on). Each sample averages the light let through
// over its own width, so unresolved gaps count as they should.
export function ringTauTable(image, samples = TAU_SAMPLES) {
  const w = image.width, h = image.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  g.drawImage(image, 0, 0);
  const px = g.getImageData(0, 0, w, h).data;
  const alpha = new Float64Array(w);
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let y = 0; y < h; y++) sum += px[(y * w + x) * 4 + 3];
    alpha[x] = sum / (h * 255);
  }
  const out = new Float32Array(samples);
  const half = 0.5 / (samples - 1);
  for (let i = 0; i < samples; i++) {
    const u = i / (samples - 1);
    const lo = Math.max(0, Math.floor((u - half) * w));
    const hi = Math.min(w, Math.ceil((u + half) * w));
    let sum = 0;
    for (let x = lo; x < hi; x++) sum += alpha[x];
    const a = sum / Math.max(1, hi - lo);
    out[i] = Math.min(TAU_MAX, -Math.log(Math.max(1e-6, 1 - a)));
  }
  return out;
}

// The ring mesh for the Earth's tilt group. radius is the Earth's authored
// radius; the tilt group's scale carries it to true scale (main.js
// applyRingShadowScale keeps planetRadius with it).
export function createEarthRingMesh({ radius, texture, sunIntensity }) {
  const inner = radius * EARTH_RING_SPAN[0];
  const outer = radius * EARTH_RING_SPAN[1];
  // 2048 segments: from the ground the outer edge is a few thousand km away,
  // where main.js's 128 would show as kinks a few pixels deep.
  const geometry = new RingGeometry(inner, outer, 2048, 1);
  const pos = geometry.attributes.position;
  const uv = geometry.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    uv.setXY(i, (Math.hypot(pos.getX(i), pos.getY(i)) - inner) / (outer - inner), 0.5);
  }
  const material = new ShaderMaterial({
    defines: { RING_SHADOW_FLOOR: '0.03', RING_SLANT: '' },
    uniforms: {
      ringTexture: { value: texture },
      sunPosition: { value: new Vector3(0, 0, 0) },
      planetCenter: { value: new Vector3() },
      planetRadius: { value: radius },
      opacity: { value: 1 },
      spokeTime: { value: 0 },
      spokeStrength: { value: 0 },
      uSunIntensity: { value: sunIntensity },
      uSunAngularRadius: { value: 0 },
      realStrip: { value: 1 },
    },
    vertexShader: saturnRingVertexShader,
    fragmentShader: saturnRingFragmentShader,
    transparent: true,
    side: DoubleSide,
    depthWrite: false,
  });
  const ring = new Mesh(geometry, material);
  ring.rotation.x = Math.PI / 2;
  ring.name = 'ring';
  ring.userData.baseRingPlanetRadius = radius;
  return ring;
}

export function setEarthRingTau(tau) {
  const table = earthRingUniforms.uRingTau.value;
  for (let i = 0; i < table.length; i++) {
    table[i].set(tau[i * 4] || 0, tau[i * 4 + 1] || 0, tau[i * 4 + 2] || 0, tau[i * 4 + 3] || 0);
  }
}

const _quat = new Quaternion();
// Every frame: the ring plane from the tilt group, the Sun's direction, and
// the drawn radius the ring mesh's own shadow uses.
export function updateEarthRingUniforms({ center, tiltGroup, ring, sunPosition }) {
  const u = earthRingUniforms;
  u.uRingCenter.value.copy(center);
  u.uRingNormal.value.set(0, 1, 0).applyQuaternion(tiltGroup.getWorldQuaternion(_quat)).normalize();
  u.uRingSunDir.value.subVectors(sunPosition, center).normalize();
  u.uRingRadius.value = ring.material.uniforms.planetRadius.value;
}
